import { createContext, useContext, useMemo, useCallback, useState, useEffect, useRef, Component } from 'react';
import { useQueryClient, useMutation, QueryClient, QueryCache, QueryClientProvider, QueryErrorResetBoundary, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { DEFAULT_RETRYABLE, DOMAIN_NAMES, OPTIMISTIC_ALLOWLIST as OPTIMISTIC_ALLOWLIST$1, CALLABLES, OPTIMISTIC_COUNTER_ALLOWLIST as OPTIMISTIC_COUNTER_ALLOWLIST$1, HTTPS_TO_APP_ERROR, ERROR_MESSAGES, ERROR_RECOVERY_HINTS } from '@levelup/api-contract';
import { jsx, jsxs } from 'react/jsx-runtime';
import { editItemKey } from '@levelup/repositories';
export { EDIT_ITEM_SCOPE, editItemKey, isSensitiveKey } from '@levelup/repositories';
import { QUESTION_TYPE_REGISTRY } from '@levelup/domain';

// src/provider/ApiProvider.tsx
var ApiContext = createContext(null);
var APP_CODE_SET = new Set(Object.keys(DEFAULT_RETRYABLE));
function readField(o, k) {
  const v = o[k];
  return typeof v === "string" ? v : void 0;
}
function asApiError(error) {
  if (error == null || typeof error !== "object") {
    return {
      code: "INTERNAL_ERROR",
      message: typeof error === "string" ? error : "Unknown error",
      retryable: DEFAULT_RETRYABLE.INTERNAL_ERROR
    };
  }
  const o = error;
  const rawCode = readField(o, "code");
  const message = readField(o, "message") ?? "Unknown error";
  const retryable = typeof o.retryable === "boolean" ? o.retryable : void 0;
  const validationErrors = Array.isArray(o.validationErrors) ? o.validationErrors : void 0;
  if (rawCode && APP_CODE_SET.has(rawCode)) {
    const code2 = rawCode;
    return {
      code: code2,
      message,
      retryable: retryable ?? DEFAULT_RETRYABLE[code2],
      validationErrors
    };
  }
  const mapped = rawCode ? HTTPS_TO_APP_ERROR[rawCode] : void 0;
  const code = mapped ?? "INTERNAL_ERROR";
  return {
    code,
    message,
    retryable: retryable ?? DEFAULT_RETRYABLE[code],
    validationErrors
  };
}

// src/error/error-policy.ts
var ALWAYS_THROW = /* @__PURE__ */ new Set([
  "PERMISSION_DENIED",
  "NOT_FOUND",
  "TENANT_SUSPENDED",
  "FEATURE_DISABLED",
  "UNAUTHENTICATED"
]);
function shouldThrowOnError(error, query) {
  const api = asApiError(error);
  if (ALWAYS_THROW.has(api.code)) return true;
  return query?.state?.data === void 0;
}
function defaultRetry(failureCount, error) {
  const api = asApiError(error);
  if (api.retryable === false) return false;
  return failureCount < 2;
}
function globalQueryErrorHandler(_error, _query) {
}

// src/provider/createQueryClient.ts
function makeQueryClient(opts) {
  const throwToBoundary = opts?.throwReadErrorsToBoundary ?? true;
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: opts?.defaultStaleTimeMs ?? 3e4,
        gcTime: opts?.defaultGcTimeMs ?? 5 * 6e4,
        retry: opts?.retry ?? defaultRetry,
        throwOnError: throwToBoundary ? (error, query) => shouldThrowOnError(error, query) : false,
        refetchOnWindowFocus: false
      },
      mutations: {
        retry: false
      }
    },
    queryCache: new QueryCache({
      onError: (error, query) => globalQueryErrorHandler()
    })
  });
}
function ApiProvider(props) {
  const {
    api,
    repos,
    transport,
    notify,
    offline,
    queryClient: providedClient,
    queryClientOptions,
    isDev,
    children
  } = props;
  const queryClient = useMemo(
    () => providedClient ?? makeQueryClient(queryClientOptions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [providedClient]
  );
  const value = useMemo(
    () => ({
      api,
      repos,
      transport,
      notify,
      offline,
      queryClient,
      isDev: isDev ?? false
    }),
    [api, repos, transport, notify, offline, queryClient, isDev]
  );
  return /* @__PURE__ */ jsx(QueryClientProvider, { client: queryClient, children: /* @__PURE__ */ jsx(ApiContext.Provider, { value, children }) });
}
function useApi() {
  const ctx = useContext(ApiContext);
  if (ctx === null) {
    throw new Error("useApi must be used within <ApiProvider>");
  }
  return ctx;
}
var useRepos = () => useApi().repos;
var useApiClient = () => useApi().api;
var useTransport = () => useApi().transport;
var useNotify = () => useApi().notify;
var useOffline = () => useApi().offline;
var useIsDev = () => useApi().isDev;
var useApiQueryClient = () => useApi().queryClient;

// src/provider/reset.ts
function resetForTenantSwitch(qc) {
  qc.clear();
}

// src/keys/key-factory.ts
function createKeyFactory(domain) {
  return {
    root: () => [domain],
    all: () => [domain],
    list: (filter) => [domain, "list", filter ?? {}],
    infinite: (filter) => [domain, "infinite", filter ?? {}],
    detail: (id) => [domain, "detail", String(id)],
    sub: (id, kind, params4) => [domain, "detail", String(id), kind, params4 ?? {}]
  };
}
var QUERY_KEYS = Object.freeze(
  Object.fromEntries(DOMAIN_NAMES.map((domain) => [domain, createKeyFactory(domain)]))
);
function keysFor(domain) {
  return QUERY_KEYS[domain];
}
var meKeys = QUERY_KEYS.me;
var tenantKeys = QUERY_KEYS.tenants;
var studentKeys = QUERY_KEYS.students;
var teacherKeys = QUERY_KEYS.teachers;
var parentKeys = QUERY_KEYS.parents;
var staffKeys = QUERY_KEYS.staff;
var classKeys = QUERY_KEYS.classes;
var sessionKeys = QUERY_KEYS.sessions;
var academicSessionKeys = QUERY_KEYS.academicSessions;
var announcementKeys = QUERY_KEYS.announcements;
var notificationKeys = QUERY_KEYS.notifications;
var notificationBadgeKeys = QUERY_KEYS.notificationBadge;
var notificationPreferencesKeys = QUERY_KEYS.notificationPreferences;
var userSearchKeys = QUERY_KEYS.userSearch;
var membershipKeys = QUERY_KEYS.memberships;
var spaceKeys = QUERY_KEYS.spaces;
var storyPointKeys = QUERY_KEYS.storyPoints;
var itemKeys = QUERY_KEYS.items;
var versionKeys = QUERY_KEYS.versions;
var questionBankKeys = QUERY_KEYS.questionBank;
var rubricPresetKeys = QUERY_KEYS.rubricPresets;
var chatKeys = QUERY_KEYS.chat;
var CONVERSATION_ROOT = QUERY_KEYS.conversations.all();
var EMPTY_CONVERSATION_PARAMS = {};
var conversationKeys = {
  all: CONVERSATION_ROOT,
  lists: () => [...CONVERSATION_ROOT, "list"],
  list: (filter = EMPTY_CONVERSATION_PARAMS) => [...CONVERSATION_ROOT, "list", filter],
  details: () => [...CONVERSATION_ROOT, "detail"],
  detail: (sessionId) => [...CONVERSATION_ROOT, "detail", String(sessionId)],
  messages: (sessionId, page = EMPTY_CONVERSATION_PARAMS) => [...CONVERSATION_ROOT, "detail", String(sessionId), "messages", page]
};
var storeKeys = QUERY_KEYS.store;
var reviewKeys = QUERY_KEYS.reviews;
var enrollmentKeys = QUERY_KEYS.enrollment;
var progressKeys = QUERY_KEYS.progress;
var testSessionKeys = QUERY_KEYS.testSessions;
var insightKeys = QUERY_KEYS.insights;
var leaderboardKeys = QUERY_KEYS.leaderboard;
var gamificationKeys = QUERY_KEYS.gamification;
var achievementKeys = QUERY_KEYS.achievements;
var levelKeys = QUERY_KEYS.levels;
var studyGoalKeys = QUERY_KEYS.studyGoals;
var studySessionKeys = QUERY_KEYS.studySessions;
var studentSummaryKeys = QUERY_KEYS.studentSummary;
var examKeys = QUERY_KEYS.exams;
var questionKeys = QUERY_KEYS.questions;
var submissionKeys = QUERY_KEYS.submissions;
var questionSubmissionKeys = QUERY_KEYS.questionSubmissions;
var evaluationSettingsKeys = QUERY_KEYS.evaluationSettings;
var deadLetterKeys = QUERY_KEYS.deadLetter;
var examAnalyticsKeys = QUERY_KEYS.examAnalytics;
var gradingReviewKeys = QUERY_KEYS.gradingReview;
var summaryKeys = QUERY_KEYS.summary;
var trendsKeys = QUERY_KEYS.trends;
var costKeys = QUERY_KEYS.cost;
var analyticsKeys = QUERY_KEYS.analytics;

// src/invalidation/derive-from-contract.ts
function dedupe(xs) {
  return Array.from(new Set(xs));
}
function buildGraphFromContract(callables, overrides) {
  const out = {};
  const merge = (fromHint, override) => {
    if (!override) return { roots: dedupe(fromHint) };
    const roots = override.replace ? dedupe(override.roots) : dedupe([...fromHint, ...override.roots]);
    return { roots, fanout: override.fanout };
  };
  for (const name of Object.keys(callables)) {
    const fromHint = callables[name].invalidates ?? [];
    out[name] = merge(fromHint, overrides[name]);
  }
  for (const name of Object.keys(overrides)) {
    if (out[name]) continue;
    out[name] = merge([], overrides[name]);
  }
  return out;
}

// src/invalidation/graph.ts
var str = (v) => typeof v === "string" ? v : String(v ?? "");
function conversationSessionId(value) {
  if (!value || typeof value !== "object") return void 0;
  const direct = value.sessionId;
  if (typeof direct === "string" && direct.length > 0) return direct;
  const session = value.session;
  return typeof session?.id === "string" && session.id.length > 0 ? session.id : void 0;
}
function conversationFanout(ctx) {
  const sessionId = conversationSessionId(ctx.data) ?? conversationSessionId(ctx.vars);
  return [conversationKeys.lists(), ...sessionId ? [conversationKeys.detail(sessionId)] : []];
}
var OVERRIDES = {
  // --- levelup: testsession / progress (server is the scoring authority) ---
  "v1.levelup.submitTestSession": {
    roots: ["progress", "spaces", "storyPoints", "analytics"],
    fanout: ({ vars, keys }) => {
      const v = vars ?? {};
      const out = [];
      if (v.spaceId !== void 0) {
        out.push(keys.spaces.detail(str(v.spaceId)));
        out.push(keys.progress.sub(str(v.spaceId), "space"));
      }
      return out;
    }
  },
  "v1.levelup.recordItemAttempt": {
    roots: ["progress"],
    fanout: ({ vars, keys }) => {
      const v = vars ?? {};
      const out = [];
      if (v.spaceId !== void 0) {
        out.push(
          keys.progress.sub(str(v.spaceId), "storyPoint", {
            storyPointId: str(v.storyPointId)
          })
        );
      }
      return out;
    }
  },
  "v1.levelup.evaluateAnswer": { roots: ["progress"] },
  // server persists progress now
  // --- levelup: content authoring ---
  "v1.levelup.saveItem": { roots: ["items", "storyPoints", "versions"] },
  "v1.levelup.saveStoryPoint": { roots: ["storyPoints", "spaces"] },
  "v1.levelup.saveSpace": { roots: ["spaces", "store"] },
  // publish mirrors store listing
  // --- levelup: store / purchase ---
  "v1.levelup.purchaseSpace": { roots: ["store", "spaces"] },
  // --- levelup: chat ---
  "v1.levelup.sendChatMessage": {
    roots: ["chat"],
    fanout: ({ vars, keys }) => {
      const v = vars ?? {};
      return v.sessionId !== void 0 ? [keys.chat.sub(str(v.sessionId), "messages")] : [];
    }
  },
  // --- levelup: server-authoritative conversations ---
  // Contract hints correctly identify the `conversations` domain. At the query
  // boundary we can do better: only history lists and the affected session
  // detail/transcript page become stale. `finish` retains the mandated progress
  // root because its response has no universally available progress key.
  "v1.levelup.startConversation": {
    roots: [],
    replace: true,
    fanout: conversationFanout
  },
  "v1.levelup.sendConversationTurn": {
    roots: [],
    replace: true,
    fanout: conversationFanout
  },
  "v1.levelup.finishConversation": {
    roots: ["progress"],
    replace: true,
    fanout: conversationFanout
  },
  "v1.levelup.abandonConversation": {
    roots: [],
    replace: true,
    fanout: conversationFanout
  },
  // --- levelup: gamification ---
  // mark-seen dirties the unlock list + the composed home (unseenCount).
  "v1.levelup.markAchievementsSeen": { roots: ["achievements", "gamification"] },
  // a goal save/archive recomputes derived progress → the composed home's
  // `activeGoals`/summary must refetch alongside the studyGoals list
  // (gamification.md §Query hooks: invalidate studyGoals + gamification summary).
  "v1.levelup.saveStudyGoal": { roots: ["studyGoals", "gamification"] },
  // authoring a badge definition dirties the catalog (+ the home's recent unlocks
  // snapshot uses the definition copy).
  "v1.levelup.saveAchievementDefinition": { roots: ["achievements", "gamification"] },
  // --- autograde ---
  "v1.autograde.gradeQuestion": {
    roots: ["questionSubmissions", "submissions", "analytics"]
  },
  "v1.autograde.uploadAnswerSheets": { roots: ["submissions", "exams"] },
  "v1.autograde.saveExam": { roots: ["exams"] },
  // --- identity ---
  "v1.identity.saveStudent": { roots: ["students", "classes"] },
  "v1.identity.saveClass": { roots: ["classes", "students", "teachers"] },
  "v1.identity.markNotificationRead": { roots: ["notifications", "notificationBadge"] },
  // --- reconciled OUTSIDE the graph: `replace:true` suppresses the contract hint ---
  // switchActiveTenant → a full `resetForTenantSwitch(qc)` clear (§4.4); the
  // contract hints `['me','claims','memberships']` but no coarse invalidate runs.
  "v1.identity.switchActiveTenant": { roots: [], replace: true },
  // generateReport → produces a signed download URL; nothing to invalidate.
  "v1.analytics.generateReport": { roots: [], replace: true }
};
var INVALIDATION_GRAPH = buildGraphFromContract(
  CALLABLES,
  OVERRIDES
);

// src/invalidation/invalidate.ts
async function invalidateForCallable(qc, name, ctx) {
  const rule = INVALIDATION_GRAPH[name];
  if (!rule) return;
  await Promise.all(
    rule.roots.map((root) => Promise.resolve(qc.invalidateQueries({ queryKey: [root] })))
  );
  if (rule.fanout && ctx) {
    const targets = rule.fanout({ vars: ctx.vars, data: ctx.data, keys: QUERY_KEYS });
    await Promise.all(
      targets.map((queryKey) => Promise.resolve(qc.invalidateQueries({ queryKey })))
    );
  }
}
var OPTIMISTIC_ALLOWLIST = OPTIMISTIC_ALLOWLIST$1;
var OPTIMISTIC_COUNTER_ALLOWLIST = OPTIMISTIC_COUNTER_ALLOWLIST$1;
var ALLOWED = new Set(OPTIMISTIC_ALLOWLIST);
function isAuthoritySensitive(name) {
  const def = CALLABLES[name];
  return def?.authoritySensitive === true;
}
function isOptimisticAllowed(name) {
  return ALLOWED.has(name);
}

// src/mutation/define-mutation.ts
function callableOf(spec) {
  const name = spec.callable ?? spec.name;
  if (!name) throw new Error("[query] defineMutation: spec.callable (or spec.name) is required.");
  return name;
}
function defineMutation(spec) {
  const callable = callableOf(spec);
  if (spec.optimistic && !isOptimisticAllowed(callable)) {
    const why = isAuthoritySensitive(callable) ? `it is authority-sensitive (grading/publish/lifecycle/purchase/session)` : `it is not on the closed OPTIMISTIC_ALLOWLIST`;
    throw new Error(
      `[query] Optimistic updates are forbidden on callable "${callable}": ${why}. See SDK-SERVER-DESIGN \xA75.5 / SDK-LAYERS-PLAN \xA74.4 / REVIEW \xA76.`
    );
  }
  return function useGeneratedMutation() {
    const { repos } = useApi();
    const qc = useQueryClient();
    const optimistic = spec.optimistic;
    return useMutation({
      mutationFn: (vars) => {
        if (!spec.run) {
          return Promise.reject(
            new Error(`[query] defineMutation("${callable}") has no run() implementation.`)
          );
        }
        return spec.run(repos, vars);
      },
      onMutate: optimistic ? async (vars) => {
        await qc.cancelQueries();
        return optimistic.apply(qc, vars, QUERY_KEYS);
      } : void 0,
      onError: (_err, _vars, ctx) => {
        if (optimistic && ctx !== void 0) {
          optimistic.rollback(qc, ctx);
        }
      },
      onSuccess: (data, vars, ctx) => {
        optimistic?.reconcile?.(qc, data, vars, ctx);
      },
      onSettled: (data, _err, vars) => {
        if (spec.invalidate !== "none") {
          void invalidateForCallable(qc, callable, { vars, data });
        }
      }
    });
  };
}

// src/mutation/optimistic.ts
function snapshot(qc, key) {
  return { key, prev: qc.getQueryData(key) };
}
function restore(qc, snap) {
  qc.setQueryData(snap.key, snap.prev);
}

// src/mutation/recipes/append-list.ts
function appendToList(rootKey, make) {
  return {
    apply: (qc, vars) => {
      const snap = snapshot(qc, rootKey);
      qc.setQueryData(rootKey, (prev) => [...prev ?? [], make(vars)]);
      return snap;
    },
    rollback: (qc, snap) => restore(qc, snap)
  };
}

// src/mutation/recipes/patch-detail.ts
function patchDetail(detailKey, patch, options) {
  return {
    apply: (qc, vars) => {
      const snap = snapshot(qc, detailKey);
      qc.setQueryData(detailKey, (prev) => patch(prev, vars));
      return snap;
    },
    rollback: (qc, snap) => restore(qc, snap),
    reconcile: options?.reconcile ? (qc, data, vars) => options.reconcile(qc, data, vars) : void 0
  };
}

// src/mutation/recipes/increment-counter.ts
function assertCounterAllowed(field) {
  if (!OPTIMISTIC_COUNTER_ALLOWLIST.includes(field)) {
    throw new Error(
      `[query] counter "${field}" is not on OPTIMISTIC_COUNTER_ALLOWLIST (${OPTIMISTIC_COUNTER_ALLOWLIST.join(", ")}). Authority counters must never be optimistically patched (SEC-08 / CONV-4).`
    );
  }
}
function incrementCounter(counterKey, delta, field = "unreadCount") {
  assertCounterAllowed(field);
  return {
    apply: (qc) => {
      const snap = snapshot(qc, counterKey);
      qc.setQueryData(counterKey, (prev) => {
        const current = prev?.[field] ?? 0;
        const next = Math.max(0, current + delta);
        return { ...prev ?? {}, [field]: next };
      });
      return snap;
    },
    rollback: (qc, snap) => restore(qc, snap)
  };
}
function decrementBadge(badgeKey2, field = "unreadCount") {
  return incrementCounter(badgeKey2, -1, field);
}
var ErrorBoundaryClass = class extends Component {
  constructor() {
    super(...arguments);
    this.state = { error: null };
    this.reset = () => {
      this.props.onReset();
      this.setState({ error: null });
    };
  }
  static getDerivedStateFromError(error) {
    return { error: asApiError(error) };
  }
  componentDidCatch(error, _info) {
    this.props.onError?.(asApiError(error));
  }
  render() {
    const { error } = this.state;
    if (error) {
      const Fallback = this.props.fallback;
      return /* @__PURE__ */ jsx(Fallback, { error, reset: this.reset });
    }
    return this.props.children;
  }
};
var DefaultApiErrorFallback = ({ error, reset }) => /* @__PURE__ */ jsxs("div", { role: "alert", "data-api-error-code": error.code, children: [
  /* @__PURE__ */ jsx("p", { children: error.message }),
  error.retryable !== false ? /* @__PURE__ */ jsx("button", { type: "button", onClick: reset, children: "Try again" }) : null
] });
function ApiErrorBoundary(props) {
  const Fallback = props.fallback ?? DefaultApiErrorFallback;
  return /* @__PURE__ */ jsx(QueryErrorResetBoundary, { children: ({ reset }) => /* @__PURE__ */ jsx(ErrorBoundaryClass, { fallback: Fallback, onReset: reset, onError: props.onError, children: props.children }) });
}
var messageFor = (code) => ERROR_MESSAGES[code];
var hintFor = (code) => ERROR_RECOVERY_HINTS[code];
function useApiError() {
  const { notify, isDev } = useApi();
  const handleError = useCallback(
    (error, fallback) => {
      const api = asApiError(error);
      const copy = api.code === "INTERNAL_ERROR" && fallback ? fallback : messageFor(api.code) ?? api.message;
      notify.error(copy, { description: hintFor(api.code) });
      if (isDev) {
        console.error(`[ApiError] ${api.code}:`, error);
      }
      return api;
    },
    [notify, isDev]
  );
  return { handleError, toApiError: asApiError };
}

// src/realtime/subscription-keys.ts
var str2 = (v) => typeof v === "string" ? v : String(v ?? "");
var SUBSCRIPTION_TARGET_KEYS = {
  // levelup
  "v1.levelup.testSessionDeadline": (p) => testSessionKeys.detail(str2(p.sessionId)),
  // CHAT-1: the payload is the {rev, lastMessageAt} BUMP, not messages — parked
  // under a dedicated `bump` sub-key so a default write can never clobber the
  // `detail`/`messages` caches. `useChatStream` always installs its own handler
  // (debounced detail-invalidate), so this factory is a raw-useSubscription
  // fallback only.
  "v1.levelup.chatStream": (p) => chatKeys.sub(str2(p.sessionId), "bump"),
  "v1.levelup.spaceProgressLive": (p) => progressKeys.sub(str2(p.spaceId), "space", { userId: str2(p.userId) }),
  "v1.levelup.leaderboardLive": (p) => leaderboardKeys.list({
    scope: str2(p.scope),
    spaceId: p.spaceId !== void 0 ? str2(p.spaceId) : void 0,
    storyPointId: p.storyPointId !== void 0 ? str2(p.storyPointId) : void 0
  }),
  "v1.levelup.studentLevelLive": () => levelKeys.detail("me"),
  "v1.levelup.achievementUnlock": () => achievementKeys.list({}),
  // autograde
  "v1.autograde.gradingStatus": (p) => submissionKeys.detail(str2(p.submissionId)),
  "v1.autograde.examGrading": (p) => examKeys.sub(str2(p.examId), "grading"),
  "v1.autograde.extractionStatus": (p) => examKeys.sub(str2(p.examId), "extraction"),
  // notification (folded onto identity)
  "v1.notification.badge": () => notificationBadgeKeys.detail("me")
};

// src/realtime/useSubscription.ts
function useSubscription(name, params4, onPayload) {
  const { transport } = useApi();
  const qc = useApiQueryClient();
  const [status, setStatus] = useState("idle");
  useEffect(() => {
    const factory = SUBSCRIPTION_TARGET_KEYS[name];
    const handle = transport.subscribe(name, params4, {
      next: (payload) => {
        setStatus("live");
        if (onPayload) {
          onPayload(payload, qc);
        } else if (factory) {
          qc.setQueryData(factory(params4), payload);
        }
      },
      error: () => setStatus("error")
    });
    return () => handle.unsubscribe();
  }, [name, JSON.stringify(params4)]);
  return { status };
}

// src/testsession-progress/keys.ts
function params(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) if (v !== void 0) out[k] = String(v);
  return out;
}
var testSessionProgressKeys = {
  /** Aggregate space progress (one learner). */
  space: (spaceId, userId) => progressKeys.sub(String(spaceId), "space", params({ userId })),
  /** Item-level story-point progress (one learner). */
  storyPoint: (spaceId, storyPointId, userId) => progressKeys.sub(String(spaceId), "storyPoint", params({ storyPointId, userId })),
  /** One live/result test-session view. */
  session: (sessionId) => testSessionKeys.detail(String(sessionId)),
  /** Paginated session history (infinite). */
  sessionList: (filter) => testSessionKeys.infinite(filter ?? {}),
  /** Paginated learning-insight stream (infinite). */
  insightList: (filter) => insightKeys.infinite(filter ?? {}),
  /** Cross-domain student summary (learner/parent dashboards). */
  studentSummary: (studentId) => studentSummaryKeys.detail(String(studentId))
};

// src/testsession-progress/repos.ts
function testSessionProgressRepos(repos) {
  return repos;
}

// src/testsession-progress/hooks.ts
function useSpaceProgress(spaceId, userId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: testSessionProgressKeys.space(String(spaceId), userId ? String(userId) : void 0),
    queryFn: () => testSessionProgressRepos(repos).progressRepo.getSpace(spaceId, userId),
    enabled: Boolean(spaceId)
  });
}
function useStoryPointProgress(spaceId, storyPointId, userId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: testSessionProgressKeys.storyPoint(
      String(spaceId),
      String(storyPointId),
      userId ? String(userId) : void 0
    ),
    queryFn: () => testSessionProgressRepos(repos).progressRepo.getStoryPoint(spaceId, storyPointId, userId),
    enabled: Boolean(spaceId && storyPointId)
  });
}
function useTestSession(sessionId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: testSessionProgressKeys.session(String(sessionId)),
    queryFn: () => testSessionProgressRepos(repos).testSessionRepo.get(sessionId),
    enabled: Boolean(sessionId)
  });
}
function useTestSessions(filter = {}) {
  const { repos } = useApi();
  return useInfiniteQuery({
    queryKey: testSessionProgressKeys.sessionList(filter),
    queryFn: ({ pageParam }) => testSessionProgressRepos(repos).testSessionRepo.list({
      ...filter,
      cursor: pageParam
    }),
    initialPageParam: void 0,
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
function useLearningInsights(filter = {}) {
  const { repos } = useApi();
  return useInfiniteQuery({
    queryKey: testSessionProgressKeys.insightList(filter),
    queryFn: ({ pageParam }) => testSessionProgressRepos(repos).learningInsightRepo.list({
      ...filter,
      cursor: pageParam
    }),
    initialPageParam: void 0,
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
function useStudentSummary(studentId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: testSessionProgressKeys.studentSummary(String(studentId)),
    queryFn: () => testSessionProgressRepos(repos).studentSummaryRepo.get(studentId),
    enabled: Boolean(studentId)
  });
}
var useStartTestSession = defineMutation({
  callable: "v1.levelup.startTestSession",
  run: (repos, vars) => testSessionProgressRepos(repos).testSessionRepo.recordStart(vars)
});
var useSubmitTestSession = defineMutation(
  {
    callable: "v1.levelup.submitTestSession",
    run: (repos, vars) => testSessionProgressRepos(repos).testSessionRepo.recordSubmit(vars)
  }
);
var useEvaluateAnswer = defineMutation({
  callable: "v1.levelup.evaluateAnswer",
  run: (repos, vars) => testSessionProgressRepos(repos).testSessionRepo.recordEvaluation(vars)
});
function makeRecordItemAttemptMutation(vars) {
  const detailKey = testSessionProgressKeys.storyPoint(
    String(vars.spaceId),
    String(vars.storyPointId)
  );
  return defineMutation({
    callable: "v1.levelup.recordItemAttempt",
    run: (repos, input) => testSessionProgressRepos(repos).progressRepo.recordAttempt(input),
    optimistic: patchDetail(
      detailKey,
      (prev, input) => {
        const prevItems = prev?.items ?? {};
        const itemId = String(input.itemId);
        const prevItem = prevItems[itemId] ?? {};
        return {
          ...prev,
          items: {
            ...prevItems,
            [itemId]: {
              ...prevItem,
              // bump the attempt counter only — NO score/correct (server scores).
              attemptsCount: (prevItem.attemptsCount ?? 0) + 1,
              inFlight: true,
              lastAnswer: input.answer
            }
          }
        };
      },
      {
        // A11/CD13: trust the authoritative server progress, write it directly.
        reconcile: (qc, data) => {
          qc.setQueryData(detailKey, (prev) => {
            const items = prev?.items ?? {};
            const progress = data.progress;
            return {
              ...prev,
              items: { ...items, [String(progress.itemId)]: progress }
            };
          });
        }
      }
    )
  });
}
var useRecordItemAttempt = defineMutation(
  {
    callable: "v1.levelup.recordItemAttempt",
    run: (repos, input) => testSessionProgressRepos(repos).progressRepo.recordAttempt(input),
    optimistic: {
      apply: (qc, vars) => {
        const key = testSessionProgressKeys.storyPoint(
          String(vars.spaceId),
          String(vars.storyPointId)
        );
        const prev = qc.getQueryData(key);
        const itemId = String(vars.itemId);
        const prevItems = prev?.items ?? {};
        const prevItem = prevItems[itemId] ?? {};
        qc.setQueryData(key, {
          ...prev,
          items: {
            ...prevItems,
            [itemId]: {
              ...prevItem,
              attemptsCount: (prevItem.attemptsCount ?? 0) + 1,
              inFlight: true,
              lastAnswer: vars.answer
            }
          }
        });
        return { key, prev };
      },
      rollback: (qc, ctx) => {
        const { key, prev } = ctx;
        qc.setQueryData(key, prev);
      },
      reconcile: (qc, data, _vars, ctx) => {
        const { key } = ctx;
        qc.setQueryData(key, (prev) => {
          const items = prev?.items ?? {};
          return { ...prev, items: { ...items, [String(data.progress.itemId)]: data.progress } };
        });
      }
    }
  }
);
var useDismissInsight = defineMutation({
  callable: "v1.levelup.dismissInsight",
  run: (repos, vars) => testSessionProgressRepos(repos).learningInsightRepo.recordDismissal(vars.insightId),
  optimistic: {
    apply: (qc, vars) => {
      const key = testSessionProgressKeys.insightList();
      const prev = qc.getQueryData(key);
      const now = (/* @__PURE__ */ new Date()).toISOString();
      qc.setQueryData(key, (p) => {
        if (!p?.pages) return p;
        return {
          ...p,
          pages: p.pages.map((page) => ({
            ...page,
            items: (page.items ?? []).map(
              (i) => i.id === String(vars.insightId) ? { ...i, dismissedAt: now } : i
            )
          }))
        };
      });
      return { key, prev };
    },
    rollback: (qc, ctx) => {
      const { key, prev } = ctx;
      qc.setQueryData(key, prev);
    }
  }
});
function useTestSessionDeadline(sessionId) {
  return useSubscription("v1.levelup.testSessionDeadline", {
    sessionId: String(sessionId)
  });
}
function useMe() {
  const { repos } = useApi();
  return useQuery({
    queryKey: meKeys.detail("me"),
    queryFn: () => repos.meRepo.get()
  });
}
function useSwitchTenant() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (targetTenantId) => repos.meRepo.switchTenant(targetTenantId),
    onSettled: () => {
      resetForTenantSwitch(qc);
    }
  });
}
function useJoinTenant() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantCode) => repos.meRepo.joinByCode(tenantCode),
    onSettled: (data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: meKeys.all() });
      void invalidateForCallable(qc, "v1.identity.joinTenant", { vars, data });
    }
  });
}
var t = (repos) => repos.tenantRepo;
function useTenants(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: tenantKeys.list(filter ?? {}),
    queryFn: () => t(repos).list(filter)
  });
}
function useTenant(tenantOverride) {
  const { repos } = useApi();
  return useQuery({
    queryKey: tenantKeys.detail(tenantOverride ?? "active"),
    queryFn: () => t(repos).get(tenantOverride)
  });
}
function useLookupTenantByCode() {
  const { repos } = useApi();
  return useMutation({ mutationFn: (code) => t(repos).lookupByCode(code) });
}
function useSaveTenant() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => t(repos).save(input),
    onSettled: (data, _err, vars) => {
      void qc.invalidateQueries({ queryKey: tenantKeys.all() });
      void invalidateForCallable(qc, "v1.identity.saveTenant", { vars, data });
    }
  });
}
function useDeactivateTenant() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v) => t(repos).deactivate(v.tenantOverride, v.reason),
    onSettled: (data, _err, vars) => {
      void invalidateForCallable(qc, "v1.identity.deactivateTenant", { vars, data });
    }
  });
}
function useReactivateTenant() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v) => t(repos).reactivate(v.tenantOverride),
    onSettled: (data, _err, vars) => {
      void invalidateForCallable(qc, "v1.identity.reactivateTenant", { vars, data });
    }
  });
}
function useExportTenantData() {
  const { repos } = useApi();
  return useMutation({ mutationFn: (input) => t(repos).exportData(input) });
}
var repoOf = (repos, name) => repos[name];
function useStudents(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: studentKeys.list(filter ?? {}),
    queryFn: () => repoOf(repos, "studentRepo").list(filter)
  });
}
function useStudent(id) {
  const { repos } = useApi();
  return useQuery({
    queryKey: studentKeys.detail(id),
    queryFn: () => repoOf(repos, "studentRepo").get(id),
    enabled: Boolean(id)
  });
}
function useSaveStudent() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => repoOf(repos, "studentRepo").save(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.saveStudent", { vars, data })
  });
}
function useTeachers(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: teacherKeys.list(filter ?? {}),
    queryFn: () => repoOf(repos, "teacherRepo").list(filter)
  });
}
function useTeacher(id) {
  const { repos } = useApi();
  return useQuery({
    queryKey: teacherKeys.detail(id),
    queryFn: () => repoOf(repos, "teacherRepo").get(id),
    enabled: Boolean(id)
  });
}
function useSaveTeacher() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => repoOf(repos, "teacherRepo").save(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.saveTeacher", { vars, data })
  });
}
function useParents(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: parentKeys.list(filter ?? {}),
    queryFn: () => repoOf(repos, "parentRepoEntity").list(filter)
  });
}
function useSaveParent() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => repoOf(repos, "parentRepoEntity").save(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.saveParent", { vars, data })
  });
}
function useStaff(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: staffKeys.list(filter ?? {}),
    queryFn: () => repoOf(repos, "staffRepo").list(filter)
  });
}
function useSaveStaff() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => repoOf(repos, "staffRepo").save(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.saveStaff", { vars, data })
  });
}
function useClasses(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: classKeys.list(filter ?? {}),
    queryFn: () => repoOf(repos, "classRepo").list(filter)
  });
}
function useClass(classId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: classKeys.detail(classId),
    queryFn: () => repos.classRepo.get({ classId }),
    enabled: Boolean(classId)
  });
}
function useSaveClass() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => repoOf(repos, "classRepo").save(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.saveClass", { vars, data })
  });
}
function useAcademicSessions(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: academicSessionKeys.list(filter ?? {}),
    queryFn: () => repoOf(repos, "academicSessionRepo").list(filter)
  });
}
function useSaveAcademicSession() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => repoOf(repos, "academicSessionRepo").save(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.saveAcademicSession", { vars, data })
  });
}
function useRolloverSession() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => repos.academicSessionRepo.rollover(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.rolloverSession", { vars, data })
  });
}
var org = (repos) => repos.orgUserRepo;
function useCreateOrgUser() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => org(repos).create(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.createOrgUser", { vars, data })
  });
}
function useBulkImportStudents() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => org(repos).bulkImportStudents(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.bulkImportStudents", { vars, data })
  });
}
function useBulkImportTeachers() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => org(repos).bulkImportTeachers(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.bulkImportTeachers", { vars, data })
  });
}
function useBulkUpdateStatus() {
  const { repos } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => org(repos).bulkUpdateStatus(input),
    onSettled: (data, _e, vars) => void invalidateForCallable(qc, "v1.identity.bulkUpdateStatus", { vars, data })
  });
}
var notif = (repos) => repos.notificationRepo;
var badgeKey = notificationBadgeKeys.detail("me");
var listKey = notificationKeys.list({});
function useNotifications(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: notificationKeys.list(filter ?? {}),
    queryFn: () => notif(repos).list(filter)
  });
}
var useMarkNotificationRead = defineMutation({
  callable: "v1.identity.markNotificationRead",
  run: (repos, vars) => notif(repos).markRead(vars.notificationId),
  optimistic: {
    apply: (qc, vars, keys) => {
      const listPatch = patchDetail(
        keys.notifications.list({}),
        (prev, v) => (Array.isArray(prev) ? prev : []).map(
          (n) => n.id === v.notificationId ? { ...n, isRead: true } : n
        )
      );
      const badge = decrementBadge(badgeKey);
      const listCtx = listPatch.apply(qc, vars, keys);
      const badgeCtx = badge.apply(qc, vars, keys);
      return { listCtx, badgeCtx };
    },
    rollback: (qc, ctx) => {
      const c = ctx;
      patchDetail(listKey, (p) => p).rollback(qc, c.listCtx);
      decrementBadge(badgeKey).rollback(qc, c.badgeCtx);
    }
  }
});
var useMarkAllNotificationsRead = defineMutation({
  callable: "v1.identity.markNotificationRead",
  run: (repos) => notif(repos).markAllRead(),
  optimistic: {
    apply: (qc, _vars, keys) => {
      const badge = patchDetail(
        keys.notificationBadge.detail("me"),
        (prev) => ({ ...prev ?? {}, unreadCount: 0 })
      );
      return { badgeCtx: badge.apply(qc, void 0, keys) };
    },
    rollback: (qc, ctx) => {
      const c = ctx;
      patchDetail(badgeKey, (p) => p).rollback(qc, c.badgeCtx);
    }
  }
});
var notif2 = (repos) => repos.notificationRepo;
function useNotificationPreferences() {
  const { repos } = useApi();
  return useQuery({
    queryKey: notificationPreferencesKeys.detail("me"),
    queryFn: () => notif2(repos).getPreferences()
  });
}
function useNotificationBadgeQuery() {
  const { repos } = useApi();
  return useQuery({
    queryKey: notificationBadgeKeys.detail("me"),
    queryFn: () => notif2(repos).getBadge()
  });
}
var useSaveNotificationPreferences = defineMutation({
  callable: "v1.identity.saveNotificationPreferences",
  run: (repos, vars) => notif2(repos).savePreferences(vars)
});
var center = (repos) => repos.notificationCenterRepo;
function useNotificationCenter(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: notificationKeys.sub("center", "inbox", filter ?? {}),
    queryFn: () => center(repos).inbox(filter)
  });
}
var ann = (repos) => repos.announcementRepo;
function useAnnouncements(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: announcementKeys.list(filter ?? {}),
    queryFn: () => ann(repos).list(filter)
  });
}
var useSaveAnnouncement = defineMutation({
  callable: "v1.identity.saveAnnouncement",
  run: (repos, vars) => ann(repos).save(vars)
});
var useMarkAnnouncementRead = defineMutation({
  callable: "v1.identity.markAnnouncementRead",
  run: (repos, vars) => ann(repos).markRead(vars.announcementId),
  optimistic: patchDetail(
    announcementKeys.list({}),
    (prev, v) => (Array.isArray(prev) ? prev : []).map(
      (a) => a.id === v.announcementId ? { ...a, isReadByMe: true } : a
    )
  )
});
function useSearchUsers(query) {
  const { repos } = useApi();
  return useQuery({
    queryKey: userSearchKeys.list({ q: query }),
    queryFn: () => repos.userSearchRepo.search(query),
    enabled: query.length > 0
  });
}
function useNotificationBadge() {
  return useSubscription("v1.notification.badge", {});
}

// src/autograde/keys.ts
var str3 = (v) => typeof v === "string" ? v : String(v ?? "");
var autogradeKeys = {
  /** Paginated exam list (infinite). */
  examList: (filter) => examKeys.infinite(filter ?? {}),
  /** One exam detail. */
  exam: (examId) => examKeys.detail(str3(examId)),
  /** An exam's extracted questions. */
  examQuestions: (examId) => questionKeys.list({ examId: str3(examId) }),
  /** Paginated submissions list (infinite, scoped by examId via filter). */
  submissionList: (filter) => submissionKeys.infinite(filter ?? {}),
  /** One submission detail. */
  submission: (submissionId) => submissionKeys.detail(str3(submissionId)),
  /** A submission's per-question grading results. */
  questionSubmissions: (submissionId) => questionSubmissionKeys.list({ submissionId: str3(submissionId) }),
  /** Evaluation-settings presets list. */
  evaluationSettings: (includePublic) => evaluationSettingsKeys.list({ includePublic: includePublic ?? false }),
  /** Paginated grading dead-letter queue (infinite). */
  deadLetterList: (filter) => deadLetterKeys.infinite(filter ?? {}),
  /** Read-only per-exam analytics projection. */
  examAnalytics: (examId) => examAnalyticsKeys.detail(str3(examId)),
  /** Cross-entity grading-review bundle (one submission). */
  gradingReviewBundle: (submissionId) => gradingReviewKeys.detail(str3(submissionId)),
  /** Cross-entity per-exam grading overview dashboard. */
  examGradingOverview: (examId) => gradingReviewKeys.sub(str3(examId), "overview")
};

// src/autograde/repos.ts
function autogradeRepos(repos) {
  return repos;
}

// src/autograde/hooks.ts
function withPageCursor(filter, pageParam) {
  const req = { ...filter ?? {} };
  if (typeof pageParam === "string") req.cursor = pageParam;
  return req;
}
function useExams(filter) {
  const { repos } = useApi();
  const examRepo = autogradeRepos(repos).examRepo;
  return useInfiniteQuery({
    queryKey: autogradeKeys.examList(filter),
    initialPageParam: null,
    queryFn: ({ pageParam }) => examRepo.list(withPageCursor(filter, pageParam)),
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
function useExam(id) {
  const { repos } = useApi();
  const examRepo = autogradeRepos(repos).examRepo;
  return useQuery({
    queryKey: autogradeKeys.exam(id),
    queryFn: () => examRepo.get(id),
    enabled: Boolean(id)
  });
}
var useSaveExam = defineMutation({
  callable: "v1.autograde.saveExam",
  run: (repos, vars) => autogradeRepos(repos).examRepo.save(vars)
});
var useExtractQuestions = defineMutation({
  callable: "v1.autograde.extractQuestions",
  run: (repos, vars) => autogradeRepos(repos).examRepo.recordExtraction(vars)
});
var useReleaseResults = defineMutation({
  callable: "v1.autograde.releaseResults",
  run: (repos, vars) => autogradeRepos(repos).examRepo.releaseResults(vars)
});
var useCreateSpaceFromExam = defineMutation({
  callable: "v1.autograde.createSpaceFromExam",
  run: (repos, vars) => autogradeRepos(repos).examRepo.createSpaceFromExam(vars)
});
function useExamQuestions(examId) {
  const { repos } = useApi();
  const examQuestionRepo = autogradeRepos(repos).examQuestionRepo;
  return useQuery({
    queryKey: autogradeKeys.examQuestions(examId),
    queryFn: () => examQuestionRepo.list(examId),
    enabled: Boolean(examId)
  });
}
var useSaveExamQuestion = defineMutation({
  callable: "v1.autograde.saveExamQuestion",
  run: (repos, vars) => autogradeRepos(repos).examQuestionRepo.saveQuestion(vars)
});
var useReExtractQuestion = defineMutation({
  callable: "v1.autograde.extractQuestions",
  run: (repos, vars) => autogradeRepos(repos).examQuestionRepo.recordReExtraction(vars.examId, vars.questionNumber)
});
function useSubmissions(filter) {
  const { repos } = useApi();
  const submissionRepo = autogradeRepos(repos).submissionRepo;
  return useInfiniteQuery({
    queryKey: autogradeKeys.submissionList(filter),
    initialPageParam: null,
    queryFn: ({ pageParam }) => submissionRepo.list(
      withPageCursor(filter, pageParam)
    ),
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
function useSubmission(id) {
  const { repos } = useApi();
  const submissionRepo = autogradeRepos(repos).submissionRepo;
  return useQuery({
    queryKey: autogradeKeys.submission(id),
    queryFn: () => submissionRepo.get(id),
    enabled: Boolean(id)
  });
}
var useUploadAnswerSheets = defineMutation({
  callable: "v1.autograde.uploadAnswerSheets",
  run: (repos, vars) => autogradeRepos(repos).submissionRepo.upload(vars)
});
var useUploadImage = defineMutation({
  callable: "v1.autograde.requestUploadUrl",
  invalidate: "none",
  run: (repos, vars) => repos.storageRepo.uploadImage(vars)
});
function useQuestionSubmissions(submissionId) {
  const { repos } = useApi();
  const questionSubmissionRepo = autogradeRepos(repos).questionSubmissionRepo;
  return useQuery({
    queryKey: autogradeKeys.questionSubmissions(submissionId),
    queryFn: () => questionSubmissionRepo.list(submissionId),
    enabled: Boolean(submissionId)
  });
}
var useGradeManual = defineMutation({
  callable: "v1.autograde.gradeQuestion",
  run: (repos, vars) => autogradeRepos(repos).questionSubmissionRepo.recordManualGrade(vars)
});
var useRetryGrading = defineMutation({
  callable: "v1.autograde.gradeQuestion",
  run: (repos, vars) => autogradeRepos(repos).questionSubmissionRepo.recordRetryGrade(vars)
});
var useAiGradeQuestion = defineMutation({
  callable: "v1.autograde.gradeQuestion",
  run: (repos, vars) => autogradeRepos(repos).questionSubmissionRepo.recordAiGrade(vars)
});
function useEvaluationSettings(includePublic) {
  const { repos } = useApi();
  const evaluationSettingsRepo = autogradeRepos(repos).evaluationSettingsRepo;
  return useQuery({
    queryKey: autogradeKeys.evaluationSettings(includePublic),
    queryFn: () => evaluationSettingsRepo.list(includePublic)
  });
}
var useSaveEvaluationSettings = defineMutation({
  callable: "v1.autograde.saveEvaluationSettings",
  run: (repos, vars) => autogradeRepos(repos).evaluationSettingsRepo.save(vars)
});
function useDeadLetterEntries(filter) {
  const { repos } = useApi();
  const deadLetterRepo = autogradeRepos(repos).deadLetterRepo;
  return useInfiniteQuery({
    queryKey: autogradeKeys.deadLetterList(filter),
    initialPageParam: void 0,
    queryFn: ({ pageParam }) => deadLetterRepo.list({
      ...filter,
      cursor: pageParam
    }),
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
var useResolveDeadLetter = defineMutation({
  callable: "v1.autograde.resolveDeadLetter",
  run: (repos, vars) => autogradeRepos(repos).deadLetterRepo.resolve(vars)
});
function useExamAnalytics(examId) {
  const { repos } = useApi();
  const examAnalyticsRepo = autogradeRepos(repos).examAnalyticsRepo;
  return useQuery({
    queryKey: autogradeKeys.examAnalytics(examId),
    queryFn: () => examAnalyticsRepo.get(examId),
    enabled: Boolean(examId)
  });
}
function useGradingReviewBundle(submissionId) {
  const { repos } = useApi();
  const gradingReviewRepo = autogradeRepos(repos).gradingReviewRepo;
  return useQuery({
    queryKey: autogradeKeys.gradingReviewBundle(submissionId),
    queryFn: () => gradingReviewRepo.getReviewBundle({ submissionId }),
    enabled: Boolean(submissionId)
  });
}
function useExamGradingOverview(examId) {
  const { repos } = useApi();
  const gradingReviewRepo = autogradeRepos(repos).gradingReviewRepo;
  return useQuery({
    queryKey: autogradeKeys.examGradingOverview(examId),
    queryFn: () => gradingReviewRepo.getExamGradingOverview({ examId }),
    enabled: Boolean(examId)
  });
}

// src/autograde/realtime.ts
function useGradingStatus(submissionId) {
  return useSubscription("v1.autograde.gradingStatus", {
    submissionId
  });
}
function useExamGradingProgress(examId) {
  return useSubscription("v1.autograde.examGrading", {
    examId
  });
}
function useExtractionProgress(examId, onPayload) {
  return useSubscription("v1.autograde.extractionStatus", { examId }, onPayload);
}

// src/analytics/keys.ts
function params2(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) if (v !== void 0) out[k] = v;
  return out;
}
var analyticsQueryKeys = {
  // ── summaries (one callable, scope-discriminated) ────────────────────────
  studentSummary: (studentId) => summaryKeys.sub("scope", "student", params2({ studentId: String(studentId) })),
  classSummary: (classId) => summaryKeys.sub("scope", "class", params2({ classId: String(classId) })),
  platformSummary: () => summaryKeys.detail("platform"),
  healthSummary: () => summaryKeys.detail("health"),
  // ── exam analytics ───────────────────────────────────────────────────────
  examAnalytics: (examId) => examAnalyticsKeys.detail(String(examId)),
  // ── insights (paginated) ─────────────────────────────────────────────────
  insightList: (filter) => insightKeys.infinite(filter ?? {}),
  // ── cost (admin) ─────────────────────────────────────────────────────────
  costList: (filter) => costKeys.list(filter ?? {}),
  // ── performance trends ───────────────────────────────────────────────────
  trends: (filter) => trendsKeys.list(filter ?? {}),
  // ── parent dashboards (keyed under the analytics root) ───────────────────
  linkedChildren: (filter) => analyticsKeys.sub("parent", "children", filter ?? {}),
  childSummary: (studentId) => analyticsKeys.sub("parent", "child", params2({ studentId: String(studentId) })),
  // ── leaderboard snapshot ─────────────────────────────────────────────────
  leaderboard: (filter) => leaderboardKeys.list(filter ?? {}),
  spaceAnalytics: (spaceId) => analyticsKeys.sub("space", "progress", { spaceId: String(spaceId) })
};

// src/analytics/repos.ts
function analyticsRepos(repos) {
  return repos;
}

// src/analytics/hooks.ts
function useClassSummary(classId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: analyticsQueryKeys.classSummary(String(classId)),
    queryFn: () => analyticsRepos(repos).summaryRepo.getClass(classId),
    enabled: Boolean(classId)
  });
}
function usePlatformSummary() {
  const { repos } = useApi();
  return useQuery({
    queryKey: analyticsQueryKeys.platformSummary(),
    queryFn: () => analyticsRepos(repos).summaryRepo.getPlatform()
  });
}
function useHealthSummary() {
  const { repos } = useApi();
  return useQuery({
    queryKey: analyticsQueryKeys.healthSummary(),
    queryFn: () => analyticsRepos(repos).summaryRepo.getHealth()
  });
}
function useSpaceAnalytics(spaceId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: analyticsQueryKeys.spaceAnalytics(spaceId),
    queryFn: () => analyticsRepos(repos).spaceAnalyticsRepo.get(spaceId),
    enabled: Boolean(spaceId)
  });
}
function useInsights(studentId, opts = {}) {
  const { repos } = useApi();
  const filter = { ...opts, studentId };
  return useInfiniteQuery({
    queryKey: analyticsQueryKeys.insightList(filter),
    queryFn: ({ pageParam }) => analyticsRepos(repos).insightRepo.list({
      ...filter,
      cursor: pageParam
    }),
    initialPageParam: void 0,
    getNextPageParam: (last) => last.nextCursor ?? void 0,
    enabled: Boolean(studentId)
  });
}
function useCostSummary(granularity, filter = {}) {
  const { repos } = useApi();
  return useQuery({
    queryKey: analyticsQueryKeys.costList({ granularity, ...filter }),
    queryFn: () => granularity === "monthly" ? analyticsRepos(repos).costRepo.listMonthly(filter) : analyticsRepos(repos).costRepo.listDaily(filter)
  });
}
function usePerformanceTrends(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: analyticsQueryKeys.trends(filter),
    queryFn: () => analyticsRepos(repos).trendsRepo.get(filter),
    enabled: Boolean(filter.granularity)
  });
}
function useLinkedChildren(filter = {}) {
  const { repos } = useApi();
  return useQuery({
    queryKey: analyticsQueryKeys.linkedChildren(filter),
    queryFn: () => analyticsRepos(repos).parentRepo.listChildren(filter)
  });
}
function useChildSummary(studentId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: analyticsQueryKeys.childSummary(String(studentId)),
    queryFn: () => analyticsRepos(repos).parentRepo.getChildSummary(studentId),
    enabled: Boolean(studentId)
  });
}
function useLeaderboard(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: analyticsQueryKeys.leaderboard(filter),
    queryFn: () => analyticsRepos(repos).leaderboardRepo.get(filter),
    enabled: Boolean(filter.scope)
  });
}
function useLeaderboardLive(filter) {
  return useSubscription("v1.levelup.leaderboardLive", filter);
}
defineMutation({
  callable: "v1.analytics.dismissInsight",
  run: (repos, vars) => analyticsRepos(repos).insightRepo.recordDismiss(vars.insightId),
  optimistic: {
    apply: (qc, vars) => {
      const key = analyticsQueryKeys.insightList();
      const prev = qc.getQueryData(key);
      const now = (/* @__PURE__ */ new Date()).toISOString();
      qc.setQueryData(key, (p) => {
        if (!p?.pages) return p;
        return {
          ...p,
          pages: p.pages.map((page) => ({
            ...page,
            items: (page.items ?? []).map(
              (i) => i.id === String(vars.insightId) ? { ...i, dismissedAt: now } : i
            )
          }))
        };
      });
      return { key, prev };
    },
    rollback: (qc, ctx) => {
      const { key, prev } = ctx;
      qc.setQueryData(key, prev);
    }
  }
});
var useGenerateReport = defineMutation({
  callable: "v1.analytics.generateReport",
  run: (repos, vars) => {
    const r = analyticsRepos(repos).reportRepo;
    switch (vars.kind) {
      case "exam":
        return r.getExamReport({ examId: vars.examId, studentId: vars.studentId });
      case "progress":
        return r.getProgressReport({ studentId: vars.studentId });
      case "class":
        return r.getClassReport({ classId: vars.classId });
    }
  }
});

// src/gamification/keys.ts
function params3(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) if (v !== void 0) out[k] = v;
  return out;
}
var self = (userId) => userId ?? "self";
var gamificationQueryKeys = {
  // ── composed gamification home (collapses 5 reads → 1) ───────────────────
  summary: (userId) => gamificationKeys.sub("home", "summary", params3({ userId: self(userId) })),
  // ── student level / XP ────────────────────────────────────────────────────
  level: (userId) => levelKeys.detail(self(userId)),
  // ── achievements: definition catalog (paginated) + caller unlocks ─────────
  catalog: (filter) => achievementKeys.infinite(filter ?? {}),
  earned: (userId, unseenOnly) => achievementKeys.sub("me", "earned", params3({ userId: self(userId), unseenOnly: !!unseenOnly })),
  // ── leaderboard snapshot (pairs with the live subscription) ───────────────
  leaderboard: (filter) => leaderboardKeys.list(filter ?? {}),
  // ── study goals (paginated planner) ───────────────────────────────────────
  goals: (filter) => studyGoalKeys.infinite(filter ?? {}),
  // ── study sessions (heatmap/streak feed) ──────────────────────────────────
  sessions: (filter) => studySessionKeys.list(filter ?? {})
};

// src/gamification/repos.ts
function gamificationRepos(repos) {
  return repos;
}

// src/gamification/hooks.ts
var str4 = (v) => v === void 0 || v === null ? void 0 : String(v);
function useGamificationSummary(userId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: gamificationQueryKeys.summary(str4(userId)),
    queryFn: () => gamificationRepos(repos).gamificationViewRepo.getSummary(userId)
  });
}
function useStudentLevel(userId) {
  const { repos } = useApi();
  return useQuery({
    queryKey: gamificationQueryKeys.level(str4(userId)),
    queryFn: () => gamificationRepos(repos).studentLevelRepo.get(userId)
  });
}
function useAchievementCatalog(filter = {}) {
  const { repos } = useApi();
  return useInfiniteQuery({
    queryKey: gamificationQueryKeys.catalog(filter),
    queryFn: ({ pageParam }) => gamificationRepos(repos).achievementRepo.listCatalog({
      ...filter,
      cursor: pageParam
    }),
    initialPageParam: void 0,
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
function useStudentAchievements(opts = {}) {
  const { repos } = useApi();
  return useInfiniteQuery({
    queryKey: gamificationQueryKeys.earned(str4(opts.userId), opts.unseenOnly),
    queryFn: ({ pageParam }) => gamificationRepos(repos).achievementRepo.listEarned({
      ...opts,
      cursor: pageParam
    }),
    initialPageParam: void 0,
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
function useLeaderboardSnapshot(filter) {
  const { repos } = useApi();
  return useQuery({
    queryKey: gamificationQueryKeys.leaderboard(filter),
    queryFn: () => gamificationRepos(repos).leaderboardRepo.getPage(filter),
    enabled: Boolean(filter.scope)
  });
}
function useStudyGoals(opts = {}) {
  const { repos } = useApi();
  return useInfiniteQuery({
    queryKey: gamificationQueryKeys.goals(opts),
    queryFn: ({ pageParam }) => gamificationRepos(repos).studyGoalRepo.list({
      ...opts,
      cursor: pageParam
    }),
    initialPageParam: void 0,
    getNextPageParam: (last) => last.nextCursor ?? void 0
  });
}
function useStudySessions(range = {}) {
  const { repos } = useApi();
  return useQuery({
    queryKey: gamificationQueryKeys.sessions(range),
    queryFn: () => gamificationRepos(repos).studySessionRepo.list(range)
  });
}
function useGamificationLeaderboardLive(filter) {
  return useSubscription("v1.levelup.leaderboardLive", filter);
}
function useAchievementUnlockStream() {
  return useSubscription("v1.levelup.achievementUnlock", {}, (_payload, qc) => {
    void qc.invalidateQueries({ queryKey: gamificationQueryKeys.earned() });
    void qc.invalidateQueries({ queryKey: gamificationQueryKeys.summary() });
  });
}
function useStudentLevelLive() {
  return useSubscription("v1.levelup.studentLevelLive", {}, (payload, qc) => {
    qc.setQueryData(gamificationQueryKeys.level(), payload);
  });
}
var useMarkAchievementsSeen = defineMutation({
  callable: "v1.levelup.markAchievementsSeen",
  run: (repos, vars) => gamificationRepos(repos).achievementRepo.markSeen(vars),
  optimistic: {
    apply: (qc, vars, keys) => {
      const earnedKey = keys.achievements.sub("me", "earned", {
        userId: "self",
        unseenOnly: false
      });
      const earnedAllUnseen = keys.achievements.sub("me", "earned", {
        userId: "self",
        unseenOnly: true
      });
      const markAll = "all" in vars && vars.all === true;
      const ids = !markAll && "achievementIds" in vars ? vars.achievementIds.map(String) : [];
      const patchPages = (prev) => {
        const p = prev;
        if (!p?.pages) return prev;
        return {
          ...p,
          pages: p.pages.map((page) => ({
            ...page,
            items: (page.items ?? []).map(
              (it) => markAll || it.id !== void 0 && ids.includes(String(it.id)) ? { ...it, seen: true } : it
            )
          }))
        };
      };
      const listPatch = patchDetail(
        earnedKey,
        (prev) => patchPages(prev)
      );
      const listUnseenPatch = patchDetail(
        earnedAllUnseen,
        (prev) => patchPages(prev)
      );
      const summaryKey = keys.gamification.sub("home", "summary", { userId: "self" });
      const delta = markAll ? -1e6 : -ids.length;
      const badge = incrementCounter(
        summaryKey,
        delta,
        "unseenCount"
      );
      return {
        listCtx: listPatch.apply(qc, vars, keys),
        listUnseenCtx: listUnseenPatch.apply(qc, vars, keys),
        badgeCtx: badge.apply(qc, vars, keys),
        earnedKey,
        earnedAllUnseen,
        summaryKey
      };
    },
    rollback: (qc, ctx) => {
      const c = ctx;
      patchDetail(c.earnedKey, (p) => p).rollback(qc, c.listCtx);
      patchDetail(c.earnedAllUnseen, (p) => p).rollback(qc, c.listUnseenCtx);
      incrementCounter(c.summaryKey, 0, "unseenCount").rollback(qc, c.badgeCtx);
    }
  }
});
var useSaveStudyGoal = defineMutation({
  callable: "v1.levelup.saveStudyGoal",
  run: (repos, vars) => gamificationRepos(repos).studyGoalRepo.save(vars)
});
var useArchiveStudyGoal = defineMutation({
  callable: "v1.levelup.saveStudyGoal",
  run: (repos, goal) => gamificationRepos(repos).studyGoalRepo.archive(goal)
});
var useSaveAchievementDefinition = defineMutation({
  callable: "v1.levelup.saveAchievementDefinition",
  run: (repos, vars) => gamificationRepos(repos).achievementRepo.saveDefinition(vars)
});
var repo = (repos, name) => repos[name];
function useSpaces(filter = {}, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: spaceKeys.list(filter),
    queryFn: () => repo(repos, "spaceRepo").list(filter),
    ...opts
  });
}
function useSpace(spaceId, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: spaceKeys.detail(spaceId),
    queryFn: () => repo(repos, "spaceRepo").get(spaceId),
    enabled: Boolean(spaceId) && (opts?.enabled ?? true),
    ...opts
  });
}
function useSpaceDetailView(spaceId, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: spaceKeys.sub(spaceId, "detailView"),
    queryFn: () => repo(repos, "spaceDetailViewRepo").get(spaceId),
    enabled: Boolean(spaceId) && (opts?.enabled ?? true),
    ...opts
  });
}
function useStoryPoints(spaceId, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: storyPointKeys.list({ spaceId }),
    queryFn: () => repo(repos, "storyPointRepo").list({ spaceId }),
    enabled: Boolean(spaceId) && (opts?.enabled ?? true),
    ...opts
  });
}
function useItems(spaceId, storyPointId, filter = {}, opts) {
  const { repos } = useApi();
  const f = { spaceId, storyPointId, ...filter };
  return useQuery({
    queryKey: itemKeys.list(f),
    queryFn: () => repo(repos, "itemRepo").list(f),
    enabled: Boolean(spaceId && storyPointId) && (opts?.enabled ?? true),
    ...opts
  });
}
function useEvaluationConfig(spaceId, itemId, opts) {
  const { repos } = useApi();
  const f = { evalConfig: true, spaceId, itemId: itemId ?? null };
  return useQuery({
    queryKey: itemKeys.list(f),
    queryFn: () => repo(repos, "itemRepo").getEvaluationConfig({ spaceId, itemId }),
    enabled: Boolean(spaceId) && (opts?.enabled ?? true),
    staleTime: 5 * 60 * 1e3,
    ...opts
  });
}
function useItemForEdit(spaceId, storyPointId, itemId, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: editItemKey(itemId),
    queryFn: () => repo(repos, "itemRepo").getForEdit({ spaceId, storyPointId, itemId }),
    enabled: Boolean(spaceId && storyPointId && itemId) && (opts?.enabled ?? true),
    gcTime: 0,
    staleTime: 0,
    ...opts
  });
}
function useVersions(spaceId, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: versionKeys.list({ spaceId }),
    queryFn: () => repo(repos, "versionRepo").list({ spaceId }),
    enabled: Boolean(spaceId) && (opts?.enabled ?? true),
    ...opts
  });
}
function useQuestionBank(filter = {}, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: questionBankKeys.list(filter),
    queryFn: () => repo(repos, "questionBankRepo").list(filter),
    ...opts
  });
}
function useRubricPresets(filter = {}, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: rubricPresetKeys.list(filter),
    queryFn: () => repo(repos, "rubricPresetRepo").list(filter),
    ...opts
  });
}
function useAgents(spaceId, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: QUERY_KEYS.agents.list({ spaceId }),
    queryFn: () => repo(repos, "agentRepo").list({ spaceId }),
    enabled: Boolean(spaceId) && (opts?.enabled ?? true),
    ...opts
  });
}
function useStoreSpaces(filter = {}, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: storeKeys.list(filter),
    queryFn: () => repo(repos, "storeRepo").listStoreSpaces(filter),
    ...opts
  });
}
function useStoreSpace(spaceId, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: storeKeys.detail(spaceId),
    queryFn: () => repo(repos, "storeRepo").getStoreSpace(spaceId),
    enabled: Boolean(spaceId) && (opts?.enabled ?? true),
    ...opts
  });
}
function useSpaceReviews(spaceId, filter = {}, opts) {
  const { repos } = useApi();
  const f = { spaceId, ...filter };
  return useQuery({
    queryKey: reviewKeys.list(f),
    queryFn: () => repo(repos, "storeRepo").listReviews(f),
    enabled: Boolean(spaceId) && (opts?.enabled ?? true),
    ...opts
  });
}
function useChatSessions(filter = {}, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: chatKeys.list(filter),
    queryFn: () => repo(repos, "chatRepo").listSessions(filter),
    ...opts
  });
}
function useChatSession(sessionId, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: chatKeys.detail(sessionId),
    queryFn: () => repo(repos, "chatRepo").getSession(sessionId),
    enabled: Boolean(sessionId) && (opts?.enabled ?? true),
    ...opts
  });
}
var conversationRepos = (repos) => repos;
function useConversations(filter = {}, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: conversationKeys.list(filter),
    queryFn: () => repos.conversationRepo.list(filter),
    ...opts
  });
}
function useConversation(sessionId, opts) {
  const { repos } = useApi();
  return useQuery({
    queryKey: conversationKeys.detail(sessionId),
    queryFn: () => repos.conversationRepo.get({
      sessionId
    }),
    enabled: Boolean(sessionId) && (opts?.enabled ?? true),
    ...opts
  });
}
var useStartConversation = defineMutation({
  callable: "v1.levelup.startConversation",
  run: (repos, variables) => conversationRepos(repos).conversationRepo.start(variables)
});
var useSendConversationTurn = defineMutation({
  callable: "v1.levelup.sendConversationTurn",
  run: (repos, variables) => conversationRepos(repos).conversationRepo.send(variables)
});
var useFinishConversation = defineMutation({
  callable: "v1.levelup.finishConversation",
  run: (repos, variables) => conversationRepos(repos).conversationRepo.finish(variables)
});
var useAbandonConversation = defineMutation({
  callable: "v1.levelup.abandonConversation",
  run: (repos, variables) => conversationRepos(repos).conversationRepo.abandon(variables)
});

// src/levelup-content/mutations.ts
var call = (repos, name, method, vars) => repos[name][method](vars);
var useSaveSpace = defineMutation({
  callable: "v1.levelup.saveSpace",
  run: (repos, vars) => call(repos, "spaceRepo", "save", vars)
});
var useSaveStoryPoint = defineMutation({
  callable: "v1.levelup.saveStoryPoint",
  run: (repos, vars) => call(repos, "storyPointRepo", "save", vars)
});
var useSaveItem = defineMutation({
  callable: "v1.levelup.saveItem",
  run: (repos, vars) => call(repos, "itemRepo", "save", vars)
});
var useImportFromBank = defineMutation({
  callable: "v1.levelup.importFromBank",
  run: (repos, vars) => call(repos, "itemRepo", "saveFromBank", vars)
});
var useSaveAgent = defineMutation({
  callable: "v1.levelup.saveAgent",
  run: (repos, vars) => call(repos, "agentRepo", "save", vars)
});
var useSaveRubricPreset = defineMutation({
  callable: "v1.levelup.saveRubricPreset",
  run: (repos, vars) => call(repos, "rubricPresetRepo", "save", vars)
});
var useSaveQuestionBankItem = defineMutation({
  callable: "v1.levelup.saveQuestionBankItem",
  run: (repos, vars) => call(repos, "questionBankRepo", "save", vars)
});
var useSendChatMessage = defineMutation({
  callable: "v1.levelup.sendChatMessage",
  run: (repos, vars) => call(repos, "chatRepo", "recordMessage", vars),
  optimistic: appendToList(
    // append into the session's messages sub-list; the chatStream subscription /
    // server response reconciles the authoritative message in onSettled.
    chatKeys.sub("session", "messages"),
    (vars) => ({
      id: `pending_${Date.now()}`,
      role: "user",
      text: vars.text,
      pending: true
    })
  )
});
var useGenerateContent = defineMutation({
  callable: "v1.levelup.generateContent",
  run: (repos, vars) => call(repos, "aiGenerationRepo", "getGeneration", vars)
});
var useSaveSpaceReview = defineMutation({
  callable: "v1.levelup.saveSpaceReview",
  run: (repos, vars) => call(repos, "storeRepo", "saveReview", vars)
});
var usePurchaseSpace = defineMutation({
  callable: "v1.levelup.purchaseSpace",
  run: (repos, vars) => call(repos, "storeRepo", "recordPurchase", vars)
});

// src/levelup-content/duplicate.ts
var call2 = (repos, name, method, vars) => repos[name][method](vars);
var useDuplicateSpace = defineMutation({
  callable: "v1.levelup.duplicateSpace",
  run: (repos, vars) => call2(repos, "spaceRepo", "duplicate", vars)
});
var CHAT_BUMP_DEBOUNCE_MS = 250;
var CONVERSATION_BUMP_DEBOUNCE_MS = CHAT_BUMP_DEBOUNCE_MS;
function createBumpRefetchHandler(queryKey, opts) {
  const delay = opts?.debounceMs ?? CHAT_BUMP_DEBOUNCE_MS;
  let timer = null;
  return {
    handle(payload, qc) {
      opts?.onPayload?.(payload, qc);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void qc.invalidateQueries({ queryKey });
      }, delay);
    },
    cancel() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }
  };
}
function createChatBumpHandler(sessionId, opts) {
  return createBumpRefetchHandler(chatKeys.detail(sessionId), opts);
}
function useChatStream(sessionId, onPayload) {
  const onPayloadRef = useRef(onPayload);
  onPayloadRef.current = onPayload;
  const handler = useMemo(
    () => createChatBumpHandler(sessionId, {
      onPayload: (p, qc) => onPayloadRef.current?.(p, qc)
    }),
    [sessionId]
  );
  useEffect(() => () => handler.cancel(), [handler]);
  return useSubscription("v1.levelup.chatStream", { sessionId }, handler.handle);
}
function createConversationBumpHandler(sessionId, opts) {
  return createBumpRefetchHandler(conversationKeys.detail(sessionId), {
    debounceMs: CONVERSATION_BUMP_DEBOUNCE_MS,
    onPayload: opts?.onPayload
  });
}
function useConversationBump(sessionId) {
  const handler = useMemo(() => createConversationBumpHandler(sessionId), [sessionId]);
  useEffect(() => () => handler.cancel(), [handler]);
  return useSubscription("v1.levelup.chatStream", { sessionId }, handler.handle);
}
function useServerTime() {
  const { transport } = useApi();
  const [offsetMs, setOffsetMs] = useState(0);
  useEffect(() => {
    const handle = transport.serverTimeOffset((ms) => setOffsetMs(ms));
    return () => handle.unsubscribe();
  }, [transport]);
  return { offsetMs, now: () => Date.now() + offsetMs };
}
var QUESTION_TYPES = Object.keys(QUESTION_TYPE_REGISTRY);
function uid() {
  return Math.random().toString(36).slice(2, 10);
}
function initialQuestionPayload(qt) {
  switch (qt) {
    case "mcq":
      return {
        questionType: "mcq",
        options: [
          { id: uid(), text: "", isCorrect: true },
          { id: uid(), text: "" }
        ],
        shuffleOptions: false
      };
    case "mcaq":
      return {
        questionType: "mcaq",
        options: [
          { id: uid(), text: "", isCorrect: true },
          { id: uid(), text: "", isCorrect: true },
          { id: uid(), text: "" }
        ],
        shuffleOptions: false
      };
    case "true-false":
      return { questionType: "true-false" };
    case "numerical":
      return { questionType: "numerical", correctAnswer: 0 };
    case "text":
      return { questionType: "text" };
    case "paragraph":
      return { questionType: "paragraph" };
    case "code": {
      return {
        questionType: "code",
        language: "python",
        testCases: [{ input: "", output: "" }]
      };
    }
    case "fill-blanks": {
      const blankId = "b1";
      return {
        questionType: "fill-blanks",
        template: `Fill in the ___${blankId}___ here`,
        blanks: [{ id: blankId, correctAnswer: "" }]
      };
    }
    case "fill-blanks-dd": {
      const blankId = "b1";
      return {
        questionType: "fill-blanks-dd",
        template: `Fill in the ___${blankId}___ here`,
        blanks: [{ id: blankId, correctAnswer: "" }],
        optionPool: ["Option A", "Option B"]
      };
    }
    case "matching":
      return {
        questionType: "matching",
        pairs: [
          { left: "", right: "" },
          { left: "", right: "" }
        ],
        shufflePairs: false
      };
    case "jumbled":
      return {
        questionType: "jumbled",
        tokens: ["", ""],
        correctOrder: [0, 1]
      };
    case "audio":
      return { questionType: "audio", maxDurationSeconds: 120 };
    case "image_evaluation":
      return { questionType: "image_evaluation" };
    case "group-options": {
      const itemId1 = uid();
      const itemId2 = uid();
      return {
        questionType: "group-options",
        groups: ["Group A", "Group B"],
        items: [
          { id: itemId1, text: "", group: "Group A" },
          { id: itemId2, text: "", group: "Group B" }
        ]
      };
    }
    case "chat_agent_question":
      return { questionType: "chat_agent_question" };
  }
}
function validateQuestionPayload(qt, payload) {
  const errors = [];
  const p = payload ?? {};
  switch (qt) {
    case "mcq":
    case "mcaq": {
      const options = Array.isArray(p.options) ? p.options : [];
      if (options.length < 2) {
        errors.push({ field: "options", message: "At least 2 options are required." });
      }
      const hasCorrect = options.some((o) => o.isCorrect === true);
      if (!hasCorrect) {
        errors.push({ field: "options", message: "At least 1 option must be marked correct." });
      }
      options.forEach((o, i) => {
        if (!o.text || o.text.trim() === "") {
          errors.push({ field: `options[${i}].text`, message: "Option text must not be empty." });
        }
      });
      if (qt === "mcaq") {
        const min = p.minSelections;
        const max = p.maxSelections;
        if (min !== void 0 && max !== void 0 && min > max) {
          errors.push({
            field: "minSelections",
            message: "minSelections must be \u2264 maxSelections."
          });
        }
      }
      break;
    }
    case "true-false": {
      if (p.correctAnswer === void 0 || p.correctAnswer === null) {
        errors.push({
          field: "correctAnswer",
          message: "correctAnswer must be set (true or false)."
        });
      }
      break;
    }
    case "numerical": {
      if (p.correctAnswer === void 0 || p.correctAnswer === null) {
        errors.push({ field: "correctAnswer", message: "correctAnswer must not be undefined." });
      }
      break;
    }
    case "fill-blanks": {
      const blanks = Array.isArray(p.blanks) ? p.blanks : [];
      if (blanks.length < 1) {
        errors.push({ field: "blanks", message: "At least 1 blank is required." });
      }
      blanks.forEach((b, i) => {
        if (!b.correctAnswer || b.correctAnswer.trim() === "") {
          errors.push({
            field: `blanks[${i}].correctAnswer`,
            message: "correctAnswer must not be empty."
          });
        }
      });
      break;
    }
    case "fill-blanks-dd": {
      const blanks = Array.isArray(p.blanks) ? p.blanks : [];
      const pool = Array.isArray(p.optionPool) ? p.optionPool : [];
      if (blanks.length < 1) {
        errors.push({ field: "blanks", message: "At least 1 blank is required." });
      }
      if (pool.length < 1) {
        errors.push({ field: "optionPool", message: "optionPool must not be empty." });
      }
      blanks.forEach((b, i) => {
        if (!b.correctAnswer || b.correctAnswer.trim() === "") {
          errors.push({
            field: `blanks[${i}].correctAnswer`,
            message: "correctAnswer must not be empty."
          });
        }
      });
      break;
    }
    case "matching": {
      const pairs = Array.isArray(p.pairs) ? p.pairs : [];
      if (pairs.length < 1) {
        errors.push({ field: "pairs", message: "At least 1 pair is required." });
      }
      pairs.forEach((pair, i) => {
        if (!pair.left || pair.left.trim() === "") {
          errors.push({ field: `pairs[${i}].left`, message: "Left side must not be empty." });
        }
        if (!pair.right || pair.right.trim() === "") {
          errors.push({ field: `pairs[${i}].right`, message: "Right side must not be empty." });
        }
      });
      break;
    }
    case "jumbled": {
      const tokens = Array.isArray(p.tokens) ? p.tokens : [];
      if (tokens.length < 2) {
        errors.push({ field: "tokens", message: "At least 2 tokens are required." });
      }
      tokens.forEach((t2, i) => {
        if (!t2 || t2.trim() === "") {
          errors.push({ field: `tokens[${i}]`, message: "Token must not be empty." });
        }
      });
      break;
    }
    case "group-options": {
      const groups = Array.isArray(p.groups) ? p.groups : [];
      const items = Array.isArray(p.items) ? p.items : [];
      if (groups.length < 2) {
        errors.push({ field: "groups", message: "At least 2 groups are required." });
      }
      if (items.length < 2) {
        errors.push({ field: "items", message: "At least 2 items are required." });
      }
      items.forEach((item, i) => {
        if (!item.group || item.group.trim() === "") {
          errors.push({
            field: `items[${i}].group`,
            message: "Every item must have a group assigned."
          });
        }
      });
      break;
    }
    case "code": {
      const testCases = Array.isArray(p.testCases) ? p.testCases : [];
      testCases.forEach((tc, i) => {
        if (!tc.output || tc.output.trim() === "") {
          errors.push({
            field: `testCases[${i}].output`,
            message: "Test case output must not be empty."
          });
        }
      });
      break;
    }
    case "image_evaluation": {
      const maxImages = p.maxImages;
      if (maxImages !== void 0 && maxImages < 1) {
        errors.push({ field: "maxImages", message: "maxImages must be \u2265 1." });
      }
      break;
    }
  }
  return errors;
}
function qfAddOption(payload) {
  const options = Array.isArray(payload.options) ? payload.options : [];
  return {
    ...payload,
    options: [...options, { id: uid(), text: "" }]
  };
}
function qfRemoveOption(payload, optionId) {
  const options = Array.isArray(payload.options) ? payload.options : [];
  return {
    ...payload,
    options: options.filter((o) => o.id !== optionId)
  };
}
function qfUpdateOption(payload, optionId, patch) {
  const options = Array.isArray(payload.options) ? payload.options : [];
  return {
    ...payload,
    options: options.map((o) => o.id === optionId ? { ...o, ...patch } : o)
  };
}
function qfMoveOptionUp(payload, optionId) {
  const options = Array.isArray(payload.options) ? [...payload.options] : [];
  const idx = options.findIndex((o) => o.id === optionId);
  if (idx <= 0) return payload;
  [options[idx - 1], options[idx]] = [options[idx], options[idx - 1]];
  return { ...payload, options };
}
function qfMoveOptionDown(payload, optionId) {
  const options = Array.isArray(payload.options) ? [...payload.options] : [];
  const idx = options.findIndex((o) => o.id === optionId);
  if (idx < 0 || idx >= options.length - 1) return payload;
  [options[idx], options[idx + 1]] = [options[idx + 1], options[idx]];
  return { ...payload, options };
}
function qfAddBlank(payload) {
  const blanks = Array.isArray(payload.blanks) ? payload.blanks : [];
  const template = typeof payload.template === "string" ? payload.template : "";
  const id = uid();
  return {
    ...payload,
    template: template + ` ___${id}___`,
    blanks: [...blanks, { id, correctAnswer: "" }]
  };
}
function qfRemoveBlank(payload, blankId) {
  const blanks = Array.isArray(payload.blanks) ? payload.blanks : [];
  const template = typeof payload.template === "string" ? payload.template : "";
  return {
    ...payload,
    template: template.replace(new RegExp(`\\s*___${blankId}___`, "g"), ""),
    blanks: blanks.filter((b) => b.id !== blankId)
  };
}
function qfUpdateBlank(payload, blankId, patch) {
  const blanks = Array.isArray(payload.blanks) ? payload.blanks : [];
  return {
    ...payload,
    blanks: blanks.map((b) => b.id === blankId ? { ...b, ...patch } : b)
  };
}
function qfAddBlankDd(payload) {
  const blanks = Array.isArray(payload.blanks) ? payload.blanks : [];
  const template = typeof payload.template === "string" ? payload.template : "";
  const id = uid();
  return {
    ...payload,
    template: template + ` ___${id}___`,
    blanks: [...blanks, { id, correctAnswer: "" }]
  };
}
function qfRemoveBlankDd(payload, blankId) {
  const blanks = Array.isArray(payload.blanks) ? payload.blanks : [];
  const template = typeof payload.template === "string" ? payload.template : "";
  return {
    ...payload,
    template: template.replace(new RegExp(`\\s*___${blankId}___`, "g"), ""),
    blanks: blanks.filter((b) => b.id !== blankId)
  };
}
function qfAddPoolOption(payload, text) {
  const pool = Array.isArray(payload.optionPool) ? payload.optionPool : [];
  return { ...payload, optionPool: [...pool, text] };
}
function qfRemovePoolOption(payload, index) {
  const pool = Array.isArray(payload.optionPool) ? payload.optionPool : [];
  return { ...payload, optionPool: pool.filter((_, i) => i !== index) };
}
function qfUpdatePoolOption(payload, index, text) {
  const pool = Array.isArray(payload.optionPool) ? [...payload.optionPool] : [];
  pool[index] = text;
  return { ...payload, optionPool: pool };
}
function qfAddPair(payload) {
  const pairs = Array.isArray(payload.pairs) ? payload.pairs : [];
  return { ...payload, pairs: [...pairs, { left: "", right: "" }] };
}
function qfRemovePair(payload, index) {
  const pairs = Array.isArray(payload.pairs) ? payload.pairs : [];
  return { ...payload, pairs: pairs.filter((_, i) => i !== index) };
}
function qfUpdatePair(payload, index, patch) {
  const pairs = Array.isArray(payload.pairs) ? [...payload.pairs] : [];
  pairs[index] = { ...pairs[index], ...patch };
  return { ...payload, pairs };
}
function qfMovePairUp(payload, index) {
  if (index <= 0) return payload;
  const pairs = Array.isArray(payload.pairs) ? [...payload.pairs] : [];
  [pairs[index - 1], pairs[index]] = [pairs[index], pairs[index - 1]];
  return { ...payload, pairs };
}
function qfMovePairDown(payload, index) {
  const pairs = Array.isArray(payload.pairs) ? [...payload.pairs] : [];
  if (index < 0 || index >= pairs.length - 1) return payload;
  [pairs[index], pairs[index + 1]] = [pairs[index + 1], pairs[index]];
  return { ...payload, pairs };
}
function qfAddToken(payload, text) {
  const tokens = Array.isArray(payload.tokens) ? payload.tokens : [];
  const correctOrder = Array.isArray(payload.correctOrder) ? payload.correctOrder : tokens.map((_, i) => i);
  const newIndex = tokens.length;
  return {
    ...payload,
    tokens: [...tokens, text],
    correctOrder: [...correctOrder, newIndex]
  };
}
function qfRemoveToken(payload, index) {
  const tokens = Array.isArray(payload.tokens) ? [...payload.tokens] : [];
  const correctOrder = Array.isArray(payload.correctOrder) ? [...payload.correctOrder] : tokens.map((_, i) => i);
  tokens.splice(index, 1);
  const newOrder = correctOrder.filter((pos) => pos !== index).map((pos) => pos > index ? pos - 1 : pos);
  return { ...payload, tokens, correctOrder: newOrder };
}
function qfUpdateToken(payload, index, text) {
  const tokens = Array.isArray(payload.tokens) ? [...payload.tokens] : [];
  tokens[index] = text;
  return { ...payload, tokens };
}
function qfMoveTokenUp(payload, index) {
  if (index <= 0) return payload;
  const tokens = Array.isArray(payload.tokens) ? [...payload.tokens] : [];
  const correctOrder = Array.isArray(payload.correctOrder) ? [...payload.correctOrder] : tokens.map((_, i) => i);
  [tokens[index - 1], tokens[index]] = [tokens[index], tokens[index - 1]];
  const newOrder = correctOrder.map((pos) => {
    if (pos === index - 1) return index;
    if (pos === index) return index - 1;
    return pos;
  });
  return { ...payload, tokens, correctOrder: newOrder };
}
function qfMoveTokenDown(payload, index) {
  const tokens = Array.isArray(payload.tokens) ? [...payload.tokens] : [];
  if (index < 0 || index >= tokens.length - 1) return payload;
  const correctOrder = Array.isArray(payload.correctOrder) ? [...payload.correctOrder] : tokens.map((_, i) => i);
  [tokens[index], tokens[index + 1]] = [tokens[index + 1], tokens[index]];
  const newOrder = correctOrder.map((pos) => {
    if (pos === index) return index + 1;
    if (pos === index + 1) return index;
    return pos;
  });
  return { ...payload, tokens, correctOrder: newOrder };
}
function qfAddGroup(payload, name) {
  const groups = Array.isArray(payload.groups) ? payload.groups : [];
  return { ...payload, groups: [...groups, name] };
}
function qfRemoveGroup(payload, name) {
  const groups = Array.isArray(payload.groups) ? payload.groups : [];
  const items = Array.isArray(payload.items) ? payload.items : [];
  return {
    ...payload,
    groups: groups.filter((g) => g !== name),
    // Unassign items that belonged to the removed group
    items: items.map((item) => item.group === name ? { ...item, group: void 0 } : item)
  };
}
function qfRenameGroup(payload, oldName, newName) {
  const groups = Array.isArray(payload.groups) ? payload.groups : [];
  const items = Array.isArray(payload.items) ? payload.items : [];
  return {
    ...payload,
    groups: groups.map((g) => g === oldName ? newName : g),
    items: items.map((item) => item.group === oldName ? { ...item, group: newName } : item)
  };
}
function qfAddGroupItem(payload, text) {
  const items = Array.isArray(payload.items) ? payload.items : [];
  return {
    ...payload,
    items: [...items, { id: uid(), text, group: "" }]
  };
}
function qfRemoveGroupItem(payload, id) {
  const items = Array.isArray(payload.items) ? payload.items : [];
  return { ...payload, items: items.filter((item) => item.id !== id) };
}
function qfUpdateGroupItem(payload, id, patch) {
  const items = Array.isArray(payload.items) ? payload.items : [];
  return {
    ...payload,
    items: items.map((item) => item.id === id ? { ...item, ...patch } : item)
  };
}
function qfAddTestCase(payload) {
  const testCases = Array.isArray(payload.testCases) ? payload.testCases : [];
  return { ...payload, testCases: [...testCases, { input: "", output: "" }] };
}
function qfRemoveTestCase(payload, index) {
  const testCases = Array.isArray(payload.testCases) ? payload.testCases : [];
  return { ...payload, testCases: testCases.filter((_, i) => i !== index) };
}
function qfUpdateTestCase(payload, index, patch) {
  const testCases = Array.isArray(payload.testCases) ? [...payload.testCases] : [];
  testCases[index] = { ...testCases[index], ...patch };
  return { ...payload, testCases };
}

export { ApiContext, ApiErrorBoundary, ApiProvider, INVALIDATION_GRAPH, OPTIMISTIC_ALLOWLIST, OPTIMISTIC_COUNTER_ALLOWLIST, QUERY_KEYS, QUESTION_TYPES, SUBSCRIPTION_TARGET_KEYS, academicSessionKeys, achievementKeys, analyticsKeys, analyticsQueryKeys, analyticsRepos, announcementKeys, appendToList, asApiError, autogradeKeys, autogradeRepos, buildGraphFromContract, chatKeys, classKeys, conversationKeys, costKeys, createKeyFactory, deadLetterKeys, decrementBadge, defaultRetry, defineMutation, enrollmentKeys, evaluationSettingsKeys, examAnalyticsKeys, examKeys, gamificationKeys, gamificationQueryKeys, gamificationRepos, globalQueryErrorHandler, gradingReviewKeys, incrementCounter, initialQuestionPayload, insightKeys, invalidateForCallable, isAuthoritySensitive, isOptimisticAllowed, itemKeys, keysFor, leaderboardKeys, levelKeys, makeQueryClient, makeRecordItemAttemptMutation, meKeys, membershipKeys, notificationBadgeKeys, notificationKeys, notificationPreferencesKeys, parentKeys, patchDetail, progressKeys, qfAddBlank, qfAddBlankDd, qfAddGroup, qfAddGroupItem, qfAddOption, qfAddPair, qfAddPoolOption, qfAddTestCase, qfAddToken, qfMoveOptionDown, qfMoveOptionUp, qfMovePairDown, qfMovePairUp, qfMoveTokenDown, qfMoveTokenUp, qfRemoveBlank, qfRemoveBlankDd, qfRemoveGroup, qfRemoveGroupItem, qfRemoveOption, qfRemovePair, qfRemovePoolOption, qfRemoveTestCase, qfRemoveToken, qfRenameGroup, qfUpdateBlank, qfUpdateGroupItem, qfUpdateOption, qfUpdatePair, qfUpdatePoolOption, qfUpdateTestCase, qfUpdateToken, questionBankKeys, questionKeys, questionSubmissionKeys, resetForTenantSwitch, restore, reviewKeys, rubricPresetKeys, sessionKeys, shouldThrowOnError, snapshot, spaceKeys, staffKeys, storeKeys, storyPointKeys, studentKeys, studentSummaryKeys, studyGoalKeys, studySessionKeys, submissionKeys, summaryKeys, teacherKeys, tenantKeys, testSessionKeys, testSessionProgressKeys, testSessionProgressRepos, trendsKeys, useAbandonConversation, useAcademicSessions, useAchievementCatalog, useAchievementUnlockStream, useAgents, useAiGradeQuestion, useAnnouncements, useApi, useApiClient, useApiError, useApiQueryClient, useArchiveStudyGoal, useBulkImportStudents, useBulkImportTeachers, useBulkUpdateStatus, useChatSession, useChatSessions, useChatStream, useChildSummary, useClass, useClassSummary, useClasses, useConversation, useConversationBump, useConversations, useCostSummary, useCreateOrgUser, useCreateSpaceFromExam, useDeactivateTenant, useDeadLetterEntries, useDismissInsight, useDuplicateSpace, useEvaluateAnswer, useEvaluationConfig, useEvaluationSettings, useExam, useExamAnalytics, useExamGradingOverview, useExamGradingProgress, useExamQuestions, useExams, useExportTenantData, useExtractQuestions, useExtractionProgress, useFinishConversation, useGamificationLeaderboardLive, useGamificationSummary, useGenerateContent, useGenerateReport, useGradeManual, useGradingReviewBundle, useGradingStatus, useHealthSummary, useImportFromBank, useInsights, useIsDev, useItemForEdit, useItems, useJoinTenant, useLeaderboard, useLeaderboardLive, useLeaderboardSnapshot, useLearningInsights, useLinkedChildren, useLookupTenantByCode, useMarkAchievementsSeen, useMarkAllNotificationsRead, useMarkAnnouncementRead, useMarkNotificationRead, useMe, useNotificationBadge, useNotificationBadgeQuery, useNotificationCenter, useNotificationPreferences, useNotifications, useNotify, useOffline, useParents, usePerformanceTrends, usePlatformSummary, usePurchaseSpace, useQuestionBank, useQuestionSubmissions, useReExtractQuestion, useReactivateTenant, useRecordItemAttempt, useReleaseResults, useRepos, useResolveDeadLetter, useRetryGrading, useRolloverSession, useRubricPresets, useSaveAcademicSession, useSaveAchievementDefinition, useSaveAgent, useSaveAnnouncement, useSaveClass, useSaveEvaluationSettings, useSaveExam, useSaveExamQuestion, useSaveItem, useSaveNotificationPreferences, useSaveParent, useSaveQuestionBankItem, useSaveRubricPreset, useSaveSpace, useSaveSpaceReview, useSaveStaff, useSaveStoryPoint, useSaveStudent, useSaveStudyGoal, useSaveTeacher, useSaveTenant, useSearchUsers, useSendChatMessage, useSendConversationTurn, useServerTime, useSpace, useSpaceAnalytics, useSpaceDetailView, useSpaceProgress, useSpaceReviews, useSpaces, useStaff, useStartConversation, useStartTestSession, useStoreSpace, useStoreSpaces, useStoryPointProgress, useStoryPoints, useStudent, useStudentAchievements, useStudentLevel, useStudentLevelLive, useStudentSummary, useStudents, useStudyGoals, useStudySessions, useSubmission, useSubmissions, useSubmitTestSession, useSubscription, useSwitchTenant, useTeacher, useTeachers, useTenant, useTenants, useTestSession, useTestSessionDeadline, useTestSessions, useTransport, useUploadAnswerSheets, useUploadImage, useVersions, userSearchKeys, validateQuestionPayload, versionKeys };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map